import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import { cloneElement, isValidElement, type ComponentProps, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Icon, type IconName } from './icon';
import { Spinner } from './spinner';
import { Tooltip } from './tooltip';

export const buttonVariants = cva(
  'relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-colors transition-fast select-none disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
  {
    variants: {
      variant: {
        // Primary follows the accent: brand colour, or the module colour inside a module.
        primary:
          'bg-accent-solid text-accent-solid-fg hover:bg-accent-solid-hover active:bg-accent-solid-hover',
        secondary:
          'border border-line-strong bg-surface text-fg shadow-raised hover:bg-hover active:bg-pressed',
        ghost: 'text-fg-muted hover:bg-hover hover:text-fg active:bg-pressed',
        danger:
          'bg-danger-solid text-danger-solid-fg hover:bg-danger-solid-hover active:bg-danger-solid-hover',
        'danger-ghost': 'text-danger hover:bg-danger-subtle active:bg-danger-subtle',
      },
      size: {
        md: 'h-control px-3.5 text-sm',
        sm: 'h-control-sm px-2.5 text-sm',
      },
      block: { true: 'w-full' },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

type ButtonVariants = VariantProps<typeof buttonVariants>;

export interface ButtonProps extends ComponentProps<'button'>, ButtonVariants {
  /** Leading icon. Replaced by a spinner while `loading`. */
  icon?: IconName;
  /** Disables the button and shows a spinner. The label stays, so the width does not jump. */
  loading?: boolean;
  /** Renders the child element (e.g. a router link) with the button's look. */
  asChild?: boolean;
  /**
   * Shows only the icon on the phone, where the label would squeeze the page title, e.g. for
   * actions in the `PageHeader`. The label stays the accessible name. Needs `icon`.
   */
  iconOnlyOnPhone?: boolean;
}

/** Wraps the label so that it is only read out on the phone; with `asChild` inside the child. */
function phoneLabel(children: ReactNode, asChild: boolean): ReactNode {
  if (asChild && isValidElement<{ children?: ReactNode }>(children)) {
    return cloneElement(children, undefined, phoneLabel(children.props.children, false));
  }
  return <span className="max-md:sr-only">{children}</span>;
}

export function Button({
  variant,
  size,
  block,
  icon,
  loading = false,
  asChild = false,
  iconOnlyOnPhone = false,
  disabled,
  className,
  children,
  type,
  ...props
}: ButtonProps) {
  const Component = asChild ? Slot.Root : 'button';
  const iconOnly = iconOnlyOnPhone && !!icon;
  return (
    <Component
      className={cn(
        buttonVariants({ variant, size, block }),
        iconOnly && 'max-md:px-0',
        iconOnly && (size === 'sm' ? 'max-md:size-control-sm' : 'max-md:size-control'),
        className,
      )}
      disabled={asChild ? undefined : disabled || loading}
      aria-busy={loading || undefined}
      type={asChild ? undefined : (type ?? 'button')}
      {...props}
    >
      {loading ? <Spinner /> : icon ? <Icon name={icon} /> : null}
      <Slot.Slottable>{iconOnly ? phoneLabel(children, asChild) : children}</Slot.Slottable>
    </Component>
  );
}

export interface IconButtonProps
  extends Omit<ComponentProps<'button'>, 'children'>, Omit<ButtonVariants, 'block'> {
  icon: IconName;
  /** Accessible name, also shown as tooltip. */
  label: string;
  /** Small counter on the icon, e.g. unread notifications. The label must contain the number too. */
  count?: number;
  loading?: boolean;
  /** Turns the icon, e.g. while a sync runs. Unlike `loading` the button stays usable. */
  spin?: boolean;
  tooltip?: boolean;
}

export function IconButton({
  icon,
  label,
  count,
  variant = 'ghost',
  size,
  loading = false,
  spin = false,
  tooltip = true,
  disabled,
  className,
  type,
  ...props
}: IconButtonProps) {
  const button = (
    <button
      type={type ?? 'button'}
      aria-label={label}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cn(
        buttonVariants({ variant, size }),
        'px-0',
        size === 'sm' ? 'size-control-sm' : 'size-control',
        className,
      )}
      {...props}
    >
      {loading ? (
        <Spinner />
      ) : (
        <Icon name={icon} className={cn('size-[1.125rem]', spin && 'animate-spin')} />
      )}
      {count !== undefined && count > 0 && (
        <span
          aria-hidden
          className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-solid px-1 text-2xs leading-none font-semibold text-accent-solid-fg tabular ring-2 ring-surface"
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
  return tooltip ? <Tooltip content={label}>{button}</Tooltip> : button;
}
